import { ErrorResponse } from './api'

interface ApiClientOptions {
  baseUrl?: string
  timeout?: number
  headers?: Record<string, string>
}

interface RefreshResponse {
  success: boolean
  user?: {
    id: string
    username: string
    email: string
  }
}

class ApiClient {
  private baseUrl: string
  private timeout: number
  private defaultHeaders: Record<string, string>
  private refreshPromise: Promise<boolean> | null = null

  constructor(options: ApiClientOptions = {}) {
    this.baseUrl = options.baseUrl || ''
    this.timeout = options.timeout || 10000
    this.defaultHeaders = options.headers || {}
  }

  private async attemptTokenRefresh(): Promise<boolean> {
    if (this.refreshPromise) {
      return this.refreshPromise
    }

    this.refreshPromise = this.performTokenRefresh()
    
    try {
      const result = await this.refreshPromise
      return result
    } finally {
      this.refreshPromise = null
    }
  }

  private getAbsoluteUrl(path: string): string {
    // If we're on the server, we need to build the absolute URL
    if (typeof window === 'undefined') {
      const protocol = process.env.NODE_ENV === 'production' ? 'https' : 'http'
      const host = process.env.VERCEL_URL || process.env.HOST || 'localhost:3000'
      return `${protocol}://${host}${path}`
    }
    return path
  }

  private async performTokenRefresh(): Promise<boolean> {
    try {
      const response = await fetch(this.getAbsoluteUrl('/api/auth/refresh'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      })

      if (!response.ok) {
        console.log('Token refresh failed:', response.status, response.statusText)
        return false
      }

      const data: RefreshResponse = await response.json()
      console.log('Token refresh successful:', data.success)
      return data.success
    } catch (error) {
      console.error('Token refresh error:', error)
      return false
    }
  }

  private async handleUnauthorized(): Promise<void> {
    try {
      await fetch(this.getAbsoluteUrl('/api/auth/logout'), {
        method: 'POST',
        credentials: 'include',
      })
    } catch (error) {
      console.error('Logout error:', error)
    }
    
    if (typeof window !== 'undefined') {
      // Avoid redirect loop if already on auth pages
      if (!window.location.pathname.startsWith('/auth')) {
        window.location.href = '/auth/login'
      }
    }
  }

  async fetch<T = any>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<{ success: true; data: T } | { success: false; error: ErrorResponse }> {
    const url = endpoint.startsWith('http') ? endpoint : `${this.baseUrl}${endpoint}`
    
    const requestOptions: RequestInit = {
      ...options,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...this.defaultHeaders,
        ...options.headers,
      },
    }

    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), this.timeout)
    requestOptions.signal = controller.signal

    try {
      const response = await fetch(url, requestOptions)
      clearTimeout(timeoutId)

      if (response.status === 401) {
        console.log('Received 401, attempting token refresh...')
        
        const refreshSuccessful = await this.attemptTokenRefresh()
        
        if (refreshSuccessful) {
          console.log('Token refresh successful, retrying original request...')
          
          const retryResponse = await fetch(url, requestOptions)
          
          if (retryResponse.status === 401) {
            console.log('Still unauthorized after refresh, logging out...')
            await this.handleUnauthorized()
            return {
              success: false,
              error: {
                error: 'unauthorized',
                message: 'Authentication required',
              },
            }
          }
          
          const retryData = await retryResponse.json()
          
          if (!retryResponse.ok) {
            return {
              success: false,
              error: {
                error: 'api_error',
                message: `HTTP ${retryResponse.status}: ${retryResponse.statusText}`,
                details: retryData,
              },
            }
          }
          
          return { success: true, data: retryData }
        } else {
          console.log('Token refresh failed, logging out...')
          await this.handleUnauthorized()
          return {
            success: false,
            error: {
              error: 'unauthorized',
              message: 'Authentication required',
            },
          }
        }
      }

      const data = await response.json()

      if (!response.ok) {
        return {
          success: false,
          error: {
            error: 'api_error',
            message: `HTTP ${response.status}: ${response.statusText}`,
            details: data,
          },
        }
      }

      return { success: true, data }
    } catch (error) {
      clearTimeout(timeoutId)
      
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          return {
            success: false,
            error: {
              error: 'timeout_error',
              message: 'Request timed out',
            },
          }
        }
        return {
          success: false,
          error: {
            error: 'network_error',
            message: error.message,
          },
        }
      }
      
      return {
        success: false,
        error: {
          error: 'unknown_error',
          message: 'An unknown error occurred',
        },
      }
    }
  }

  async get<T = any>(endpoint: string, options: RequestInit = {}) {
    return this.fetch<T>(endpoint, { ...options, method: 'GET' })
  }

  async post<T = any>(endpoint: string, data?: any, options: RequestInit = {}) {
    return this.fetch<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    })
  }

  async put<T = any>(endpoint: string, data?: any, options: RequestInit = {}) {
    return this.fetch<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    })
  }

  async patch<T = any>(endpoint: string, data?: any, options: RequestInit = {}) {
    return this.fetch<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    })
  }

  async delete<T = any>(endpoint: string, options: RequestInit = {}) {
    return this.fetch<T>(endpoint, { ...options, method: 'DELETE' })
  }
}

export const apiClient = new ApiClient()

// Simple client for external APIs without token refresh logic
class SimpleApiClient {
  private baseUrl: string
  private timeout: number
  private defaultHeaders: Record<string, string>

  constructor(options: ApiClientOptions = {}) {
    this.baseUrl = options.baseUrl || ''
    this.timeout = options.timeout || 10000
    this.defaultHeaders = options.headers || {}
  }

  async fetch<T = any>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<{ success: true; data: T } | { success: false; error: ErrorResponse }> {
    const url = endpoint.startsWith('http') ? endpoint : `${this.baseUrl}${endpoint}`
    
    const requestOptions: RequestInit = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...this.defaultHeaders,
        ...options.headers,
      },
    }

    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), this.timeout)
    requestOptions.signal = controller.signal

    try {
      const response = await fetch(url, requestOptions)
      clearTimeout(timeoutId)

      const data = await response.json()

      if (!response.ok) {
        return {
          success: false,
          error: {
            error: 'api_error',
            message: `HTTP ${response.status}: ${response.statusText}`,
            details: data,
          },
        }
      }

      return { success: true, data }
    } catch (error) {
      clearTimeout(timeoutId)
      
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          return {
            success: false,
            error: {
              error: 'timeout_error',
              message: 'Request timed out',
            },
          }
        }
        return {
          success: false,
          error: {
            error: 'network_error',
            message: error.message,
          },
        }
      }
      
      return {
        success: false,
        error: {
          error: 'unknown_error',
          message: 'An unknown error occurred',
        },
      }
    }
  }

  async post<T = any>(endpoint: string, data?: any, options: RequestInit = {}) {
    return this.fetch<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    })
  }
}

export const externalApiClient = new SimpleApiClient({
  baseUrl: process.env.NEXT_PUBLIC_AUTH_API_URL || process.env.AUTH_API_URL || 'https://api.irc.ninja/api/v1',
  timeout: 10000,
})